import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import useEnableTwoFactor from '@/services/api/auth/tfa/useEnableTwoFactor'
import useGetTwoFactorQrCode from '@/services/api/auth/tfa/useGetTwoFactorQrCode'
import useGetTwoFactorSecretKey from '@/services/api/auth/tfa/useGetTwoFactorSecretKey'
import useConfirmTwoFactor from '@/services/api/auth/tfa/useConfirmTwoFactor'
import {
  Shield,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  Download,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Field from '@/components/ui/Field'
import FieldStatusMessage from '@/components/ui/FieldStatusMessage'
import useFetchTwoFactorRecoveryCodes from '@/services/api/auth/tfa/useFetchTwoFactorRecoveryCodes'
import { PasswordInput } from '@/components/PasswordInput'

interface TwoFactorSetupDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

type SetupStep = 'password' | 'setup' | 'confirm' | 'recovery'

export default function TwoFactorSetupDialog({
  open,
  onOpenChange,
  onSuccess,
}: TwoFactorSetupDialogProps) {
  const [currentStep, setCurrentStep] = useState<SetupStep>('password')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [copiedCodes, setCopiedCodes] = useState(false)

  const enableTwoFactor = useEnableTwoFactor()

  const qrCode = useGetTwoFactorQrCode()
  const secretKey = useGetTwoFactorSecretKey()
  const fetchTwoFactorRecoveryCodes = useFetchTwoFactorRecoveryCodes()

  // Form schemas
  const passwordSchema = z.object({
    password: z.string().min(1, 'Password is required'),
  })

  const confirmationSchema = z.object({
    code: z
      .string()
      .length(6, 'Code must be 6 digits')
      .min(1, 'Verification code is required'),
  })

  // Forms
  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '' },
    mode: 'onSubmit',
  })

  const confirmationForm = useForm({
    resolver: zodResolver(confirmationSchema),
    defaultValues: { code: '' },
    mode: 'onSubmit',
  })

  const confirmTwoFactor = useConfirmTwoFactor()

  const handlePasswordSubmit = async (values: { password: string }) => {
    passwordForm.clearErrors()
    await enableTwoFactor.action({ password: values.password })
    setCurrentStep('setup')
    await qrCode.action()
    await secretKey.action()
  }

  const handleConfirmationSubmit = async (values: { code: string }) => {
    await confirmTwoFactor.action({ code: values.code })
    const { data } = await fetchTwoFactorRecoveryCodes.action()
    setRecoveryCodes(data)
    setCurrentStep('recovery')
  }

  const handleComplete = () => {
    onSuccess()
    handleClose()
  }

  const handleClose = () => {
    onOpenChange(false)
    setTimeout(() => {
      setCurrentStep('password')
      passwordForm.reset()
      confirmationForm.reset()
      setRecoveryCodes([])
      setCopiedCodes(false)
    }, 200)
  }

  const copySecretKey = () => {
    if (secretKey.status.status === 'success') {
      navigator.clipboard.writeText(secretKey.status.data)
      toast.success('Secret key copied to clipboard')
    }
  }

  const copyRecoveryCodes = () => {
    const codesText = recoveryCodes.join('\n')
    navigator.clipboard.writeText(codesText)
    setCopiedCodes(true)
    toast.success('Recovery codes copied to clipboard')
    setTimeout(() => setCopiedCodes(false), 2000)
  }

  const downloadRecoveryCodes = () => {
    const codesText = recoveryCodes.join('\n')
    const blob = new Blob([codesText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = '2fa-recovery-codes.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success('Recovery codes downloaded')
  }

  const canProceedToConfirm =
    qrCode.status.status === 'success' && secretKey.status.status === 'success'

  const renderPasswordStep = () => (
    <form
      onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)}
      className="space-y-4"
    >
      <div className="mb-4 text-center">
        <p className="text-sm  text-txt-secondary">
          Please confirm your password to enable two-factor authentication.
        </p>
      </div>

      <Field
        statusText={passwordForm.formState.errors?.password?.message?.toString()}
        status={passwordForm.formState.errors?.password ? 'error' : null}
      >
        <Label htmlFor="password">Current Password</Label>
        <div className="relative">
          <PasswordInput
            id="password"
            {...passwordForm.register('password')}
            placeholder="Enter your password"
          />
        </div>
        <FieldStatusMessage />
      </Field>

      <div className="flex gap-3">
        <Button
          type="submit"
          disabled={enableTwoFactor.status.status === 'pending'}
          className="flex-1"
        >
          {enableTwoFactor.status.status === 'pending'
            ? 'Enabling 2FA...'
            : 'Enable 2FA'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={enableTwoFactor.status.status === 'pending'}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </form>
  )

  const renderSetupStep = () => (
    <div className="space-y-4">
      {qrCode.status.status === 'pending' ||
      secretKey.status.status === 'pending' ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <Loader2 className="mx-auto mb-2 size-6 animate-spin" />
            <p className="text-sm text-txt-secondary">
              Setting up your authenticator...
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4 text-center">
            <p className="text-sm text-txt-secondary">
              Scan this QR code with your authenticator app (Google
              Authenticator, Authy, etc.)
            </p>
          </div>

          <div className="text-center">
            {qrCode.status.data ? (
              <div className="mb-4 inline-block rounded-lg border bg-white p-4">
                {/* Render svg element */}
                <svg
                  dangerouslySetInnerHTML={{ __html: qrCode.status.data.svg }}
                  className="h-48 w-48"
                />
              </div>
            ) : (
              <div className="mb-4 inline-block h-48 w-48 items-center justify-center rounded-lg border bg-gray-100 p-4">
                <div className="text-center">
                  <p className="mb-2 text-sm text-txt-secondary">
                    Failed to generate QR code
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => qrCode.action()}
                    disabled={qrCode.status.status === 'error'}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Can't scan? Enter this key manually:</Label>
            <div className="flex items-center gap-2">
              <Input
                value={secretKey.status.data.secretKey || ''}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={copySecretKey}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => setCurrentStep('confirm')}
              disabled={!canProceedToConfirm}
              className="flex-1"
            >
              I've Added This Account
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </div>
  )

  const renderConfirmStep = () => (
    <form
      onSubmit={confirmationForm.handleSubmit(handleConfirmationSubmit)}
      className="space-y-4"
    >
      <div className="mb-4 text-center">
        <p className="text-sm text-txt-secondary">
          Enter the 6-digit code from your authenticator app to verify the
          setup.
        </p>
      </div>

      <Field
        statusText={confirmationForm.formState.errors?.code?.message?.toString()}
        status={confirmationForm.formState.errors?.code ? 'error' : null}
      >
        <Label htmlFor="code">Verification Code</Label>
        <Input
          id="code"
          {...confirmationForm.register('code')}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 6)
            confirmationForm.setValue('code', value)
            if (confirmationForm.formState.errors.code) {
              confirmationForm.clearErrors('code')
            }
          }}
          placeholder="000000"
          className="text-center text-lg tracking-widest"
          maxLength={6}
        />
        <FieldStatusMessage />
      </Field>

      <div className="flex gap-3">
        <Button
          type="submit"
          disabled={confirmTwoFactor.status.status === 'pending'}
          className="flex-1"
        >
          {confirmTwoFactor.status.status === 'pending'
            ? 'Verifying...'
            : 'Verify & Complete'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setCurrentStep('setup')}
          disabled={confirmTwoFactor.status.status === 'pending'}
          className="flex-1"
        >
          Back
        </Button>
      </div>
    </form>
  )

  const renderRecoveryStep = () => (
    <div className="space-y-4">
      <div className="mb-4 text-center">
        <CheckCircle className="mx-auto mb-3 h-12 w-12 text-txt-success" />
        <h3 className="mb-2 text-lg font-medium">2FA Setup Complete!</h3>
        <p className="text-sm text-txt-secondary">
          Save these recovery codes now. You won't see them again.
        </p>
      </div>

      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600" />
          <div className="text-sm text-yellow-800">
            <p className="mb-2">
              <strong>Important:</strong> Save these recovery codes in a secure
              location.
            </p>
            <p>
              If you lose access to your authenticator app, these codes are the
              only way to regain access to your account.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-gray-50 p-4">
        <div className="font-mono grid grid-cols-2 gap-2 text-sm">
          {recoveryCodes.map((code, index) => (
            <div key={index} className="py-1 text-center">
              {code}
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          onClick={copyRecoveryCodes}
          variant="outline"
          className="flex-1"
        >
          {copiedCodes ? (
            <>
              <CheckCircle className="mr-2 h-4 w-4" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="mr-2 h-4 w-4" />
              Copy Codes
            </>
          )}
        </Button>
        <Button
          onClick={downloadRecoveryCodes}
          variant="outline"
          className="flex-1"
        >
          <Download className="mr-2 h-4 w-4" />
          Download
        </Button>
      </div>

      <Button onClick={handleComplete} className="w-full">
        I've Saved My Recovery Codes
      </Button>
    </div>
  )

  const getStepTitle = () => {
    switch (currentStep) {
      case 'password':
        return 'Confirm Password'
      case 'setup':
        return 'Set up Authenticator App'
      case 'confirm':
        return 'Verify Setup'
      case 'recovery':
        return 'Save Recovery Codes'
      default:
        return 'Set up Two-Factor Authentication'
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {getStepTitle()}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {currentStep === 'password' && renderPasswordStep()}
          {currentStep === 'setup' && renderSetupStep()}
          {currentStep === 'confirm' && renderConfirmStep()}
          {currentStep === 'recovery' && renderRecoveryStep()}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
